import {
  Autocomplete as MuiAutocomplete,
  AutocompleteFreeSoloValueMapping,
  AutocompleteProps as MuiAutocompleteProps,
} from '@mui/material';
import type { ForwardedRef, JSX } from 'react';
import { forwardRef } from 'react';
import VirtualizedAutocomplete from './VirtualizedAutocomplete';

interface AutocompleteProps<
  Value,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> extends MuiAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo> {
  disableUniqueLabel?: boolean;
  disableVirtualization?: boolean;
}

function Autocomplete<
  Value,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  {
    disableUniqueLabel = false,
    disableVirtualization = false,
    ...props
  }: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>,
  ref: ForwardedRef<any>
): JSX.Element {
  const getOriginalOptionLabel = (
    option: Value | AutocompleteFreeSoloValueMapping<FreeSolo>
  ) =>
    props.getOptionLabel
      ? props.getOptionLabel(option)
      : typeof option === 'string'
        ? (option as string)
        : '';

  const getOptionLabel = (
    option: Value | AutocompleteFreeSoloValueMapping<FreeSolo>
  ) => {
    const label: string = getOriginalOptionLabel(option);
    if (disableUniqueLabel) {
      return label;
    }

    const duplicates: Value[] = props.options.filter(
      (o) => getOriginalOptionLabel(o) === label
    );
    const index = duplicates.findIndex((o) => o === option) + 1;

    return duplicates.length === 1 || index < 1 ? label : `${label} (${index})`;
  };

  const autocompleteProps = {
    ...props,
    getOptionLabel,
    slotProps: {
      ...props.slotProps,
      paper: { ...props.slotProps?.paper, sx: { boxShadow: 8 } },
    },
  };

  // Virtualize autocomplete fields with grouping is not supported. Will likely
  // be possible, as outlined in docs, after upgrading MUI:
  // https://mui.com/material-ui/react-autocomplete/#virtualization
  if (!disableVirtualization && props.groupBy === undefined) {
    return <VirtualizedAutocomplete {...autocompleteProps} ref={ref} />;
  }

  return <MuiAutocomplete {...autocompleteProps} ref={ref} />;
}

export default forwardRef(Autocomplete);
